import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';

// Well-known feature keys consumed across the app. Keep the list in one
// place so the loader, the admin Settings page, and the gates all agree.
export const FEATURE_KEYS = {
  doctorAllPatientMode: 'doctor.allPatientMode',
  // doctor.patientLibrary removed (India-SaaS decisions log #18).
  // patient.allDoctorMode, patient.doctorLibrary and patient.familyAccounts
  // retired (owner, 1 Oct 2026): family accounts are always on; the other two
  // were removed together with the UI they gated.
  // Multi-value (not boolean) setting: how far in the future a doctor may apply
  // for leave. Stored in the feature row's `value` column as one of
  // '3' | '6' | '9' | '12' | 'none' (months, or 'none' = no restriction).
  // The leave date picker reads this to cap its max selectable date.
  doctorLeaveFutureLimit: 'doctor.leaveFutureLimit',
  // Gate the public PATIENT registration pointers. When inactive, the
  // Home/Login CTAs and the patient card in RegisterChoice are hidden.
  // Default-on so existing deployments still allow sign-up until an admin
  // explicitly disables it. (Doctor registration is no longer a platform flag:
  // each tenant says so itself — hooks/useDoctorRegistrationOpen.)
  // NOTE: patient self-registration itself moved to the MOC patient portal
  // (owner decision — isolated-tenant patients get accounts at the desk).
  // authPatientRegistration now only gates the signpost CTAs/card pointing
  // there (they also need VITE_PATIENT_WEB_URL to be set), and
  // /patient-register is a permanent "moved" notice.
  authPatientRegistration: 'auth.patientRegistration',
  // doctor.medicines retired (owner, 1 Oct 2026): Medicines is always on; the
  // only gate left is the TENANT feature 'medicines' (useTenantFeatures).
  // NOTE: the login-identifier policy (auth.loginIdentifier + its two OTP
  // sub-options) is PER HOSPITAL and deliberately NOT in this slice — the
  // login/registration pages read each tenant's `loginPolicy` from
  // GET /tenants/public (see utils/loginPolicy.js), and the Super Admin sets
  // it from Tenants → Features.
};

const ALL_KEYS = Object.values(FEATURE_KEYS);

// Default-on for doctor flags (these features were live before the flag was
// introduced) and default-off for patient flags (their UI was previously
// commented out). Used while flags are still loading and as the fallback
// when the backend returns no row for a key.
const DEFAULT_STATUS = {
  [FEATURE_KEYS.doctorAllPatientMode]: 'active',
  [FEATURE_KEYS.doctorLeaveFutureLimit]: 'active',
  [FEATURE_KEYS.authPatientRegistration]: 'active',
};

// Fallback values for multi-value (non-boolean) settings, used while flags load
// and when the backend has no row/value yet. Boolean flags stay null here.
const DEFAULT_VALUE = {
  [FEATURE_KEYS.doctorLeaveFutureLimit]: '12',
};

// Ordered stops for the "leave apply future upto" setting. The admin Settings
// slider renders these left→right; the leave picker maps the chosen `value` to
// a max selectable date. `months: null` means "no restriction".
export const LEAVE_FUTURE_LIMIT_OPTIONS = [
  { value: '3', months: 3 },
  { value: '6', months: 6 },
  { value: '9', months: 9 },
  { value: '12', months: 12 },
  { value: 'none', months: null },
];


export const fetchFeatureFlags = createAsyncThunk(
  'feature/fetchFeatureFlags',
  async (_, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const response = await axiosInstance.get(
        `${baseURL}/feature/by-keys?keys=${encodeURIComponent(ALL_KEYS.join(','))}`
      );
      return response.data?.features || [];
    } catch (error) {
      return rejectWithValue({
        error: error.response?.data?.message || error.message || 'Failed to load feature flags',
      });
    }
  }
);

export const toggleFeatureStatus = createAsyncThunk(
  'feature/toggleFeatureStatus',
  async ({ featureId, fekey }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const response = await axiosInstance.post(
        `${baseURL}/feature/${featureId}/toggle-status`
      );
      return { fekey, feature: response.data?.feature };
    } catch (error) {
      return rejectWithValue({
        error: error.response?.data?.message || error.message || 'Failed to toggle feature',
      });
    }
  }
);

// Persist the scalar value of a multi-value setting (e.g. the leave future
// limit). Boolean flags keep using toggleFeatureStatus instead.
export const setFeatureValue = createAsyncThunk(
  'feature/setFeatureValue',
  async ({ featureId, fekey, value }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const response = await axiosInstance.post(
        `${baseURL}/feature/${featureId}/value`,
        { value }
      );
      return { fekey, feature: response.data?.feature };
    } catch (error) {
      return rejectWithValue({
        error: error.response?.data?.message || error.message || 'Failed to update setting',
      });
    }
  }
);

const initialState = {
  // Map of fekey → { status: 'active'|'inactive', featureId, value }
  flags: Object.fromEntries(
    ALL_KEYS.map((k) => [k, { status: DEFAULT_STATUS[k], featureId: null, value: DEFAULT_VALUE[k] ?? null }])
  ),
  loaded: false,
  loading: false,
  toggling: false,
  settingValue: false,
  error: null,
};

const featureSlice = createSlice({
  name: 'feature',
  initialState,
  reducers: {
    resetFeatureFlags: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeatureFlags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeatureFlags.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        for (const row of action.payload) {
          if (!row || !row.fekey) continue;
          // Backend returns status: null when no row exists yet for that key.
          // Fall back to the documented default rather than leaving it null.
          state.flags[row.fekey] = {
            status: row.status || DEFAULT_STATUS[row.fekey] || 'inactive',
            featureId: row.featureId || null,
            value: row.value ?? DEFAULT_VALUE[row.fekey] ?? null,
          };
        }
      })
      .addCase(fetchFeatureFlags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to load feature flags';
      })
      .addCase(toggleFeatureStatus.pending, (state) => {
        state.toggling = true;
      })
      .addCase(toggleFeatureStatus.fulfilled, (state, action) => {
        state.toggling = false;
        const { fekey, feature } = action.payload || {};
        if (fekey && feature) {
          state.flags[fekey] = {
            status: feature.status,
            featureId: feature.featureId,
            value: feature.value ?? DEFAULT_VALUE[fekey] ?? null,
          };
        }
      })
      .addCase(toggleFeatureStatus.rejected, (state, action) => {
        state.toggling = false;
        state.error = action.payload?.error || 'Failed to toggle feature';
      })
      .addCase(setFeatureValue.pending, (state) => {
        state.settingValue = true;
      })
      .addCase(setFeatureValue.fulfilled, (state, action) => {
        state.settingValue = false;
        const { fekey, feature } = action.payload || {};
        if (fekey && feature) {
          state.flags[fekey] = {
            status: feature.status,
            featureId: feature.featureId,
            value: feature.value ?? DEFAULT_VALUE[fekey] ?? null,
          };
        }
      })
      .addCase(setFeatureValue.rejected, (state, action) => {
        state.settingValue = false;
        state.error = action.payload?.error || 'Failed to update setting';
      });
  },
});

export const { resetFeatureFlags } = featureSlice.actions;

// Selector helper used by gating call-sites.
export const isFeatureEnabled = (state, fekey) =>
  state?.feature?.flags?.[fekey]?.status === 'active';

// Read a multi-value setting's stored value, falling back to its default.
export const getFeatureValue = (state, fekey) =>
  state?.feature?.flags?.[fekey]?.value ?? DEFAULT_VALUE[fekey] ?? null;

export default featureSlice.reducer;
